/**
 * useVoiceRecording Hook
 * Manages audio recording lifecycle using MediaRecorder API
 * Includes automatic silence detection for hands-free recording
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Configuration for voice recording
 * Can be adjusted for different silence detection thresholds
 */
export const VOICE_RECORDING_CONFIG = {
  /** Silence timeout in milliseconds - recording stops after this period of silence */
  SILENCE_TIMEOUT_MS: 2000, // 2 seconds default
  /** Minimum audio level threshold (0-1) to consider as "not silent" */
  SILENCE_THRESHOLD: 0.01,
  /** How often to check for silence (ms) */
  SILENCE_CHECK_INTERVAL: 100,
} as const;

interface UseVoiceRecordingReturn {
  isRecording: boolean;
  isPermissionGranted: boolean;
  error: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<{ audioData: string; format: string } | null>;
  requestPermission: () => Promise<boolean>;
  /** Callback when recording stops automatically due to silence */
  onSilenceDetected?: (audioData: string, format: string) => void;
}

/**
 * Convert audio blob to base64 string
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      // Remove data URL prefix (data:audio/webm;base64,)
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

interface UseVoiceRecordingOptions {
  /** Callback when recording stops automatically due to silence */
  onSilenceDetected?: (audioData: string, format: string) => void;
}

export function useVoiceRecording(options?: UseVoiceRecordingOptions): UseVoiceRecordingReturn {
  const { onSilenceDetected } = options || {};
  const [isRecording, setIsRecording] = useState(false);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceCheckIntervalRef = useRef<number | null>(null);
  const lastSoundTimeRef = useRef<number>(0);

  /**
   * Request microphone permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach((track) => track.stop());
      setIsPermissionGranted(true);
      return true;
    } catch (err) {
      console.error('[useVoiceRecording] Permission denied:', err);
      setIsPermissionGranted(false);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError('Microphone permission denied. Please enable in browser settings.');
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          setError('Microphone not available. Please check your device.');
        } else {
          setError('Failed to access microphone. Please try again.');
        }
      }
      return false;
    }
  }, []);

  /**
   * Stop recording and return base64 audio data (internal implementation)
   */
  const stopRecordingInternal = useCallback(async (): Promise<{ audioData: string; format: string } | null> => {
    try {
      // Clear silence detection interval
      if (silenceCheckIntervalRef.current !== null) {
        clearInterval(silenceCheckIntervalRef.current);
        silenceCheckIntervalRef.current = null;
      }

      // Cleanup AudioContext
      if (audioContextRef.current) {
        try {
          await audioContextRef.current.close();
        } catch (err) {
          console.warn('[useVoiceRecording] Error closing AudioContext:', err);
        }
        audioContextRef.current = null;
        analyserRef.current = null;
      }

      if (!mediaRecorderRef.current || !isRecording) {
        return null;
      }

      return new Promise((resolve) => {
        const mediaRecorder = mediaRecorderRef.current!;

        mediaRecorder.onstop = async () => {
          try {
            // Combine all audio chunks
            const audioBlob = new Blob(audioChunksRef.current, {
              type: mediaRecorder.mimeType || 'audio/webm',
            });

            // Convert to base64
            const audioData = await blobToBase64(audioBlob);

            // Determine format from MIME type
            let format = 'webm';
            if (mediaRecorder.mimeType.includes('wav')) {
              format = 'wav';
            } else if (mediaRecorder.mimeType.includes('mp3')) {
              format = 'mp3';
            }

            // Cleanup
            if (mediaStreamRef.current) {
              mediaStreamRef.current.getTracks().forEach((track) => track.stop());
              mediaStreamRef.current = null;
            }
            mediaRecorderRef.current = null;
            audioChunksRef.current = [];
            setIsRecording(false);

            resolve({ audioData, format });
          } catch (err) {
            console.error('[useVoiceRecording] Failed to process audio:', err);
            setError('Failed to process recording. Please try again.');
            setIsRecording(false);
            resolve(null);
          }
        };

        // Stop recording
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
        }
        // Note: onstop handler will be called automatically when stop() completes
      });
    } catch (err) {
      console.error('[useVoiceRecording] Failed to stop recording:', err);
      setError('Failed to stop recording. Please try again.');
      setIsRecording(false);
      return null;
    }
  }, [isRecording]);

  /**
   * Check for silence and auto-stop if silence timeout exceeded
   */
  const checkForSilence = useCallback(async () => {
    if (!analyserRef.current || !isRecording) {
      return;
    }

    try {
      const analyser = analyserRef.current;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyser.getByteFrequencyData(dataArray);

      // Calculate average volume
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const average = sum / bufferLength;
      const normalizedVolume = average / 255; // Normalize to 0-1

      const now = Date.now();

      // If volume is above threshold, update last sound time
      if (normalizedVolume > VOICE_RECORDING_CONFIG.SILENCE_THRESHOLD) {
        lastSoundTimeRef.current = now;
      } else {
        // Check if silence timeout has been exceeded
        const silenceDuration = now - lastSoundTimeRef.current;
        if (silenceDuration >= VOICE_RECORDING_CONFIG.SILENCE_TIMEOUT_MS) {
          // Stop recording and send audio automatically
          console.log('[useVoiceRecording] Silence detected, auto-stopping recording');
          const result = await stopRecordingInternal();
          if (result && onSilenceDetected) {
            onSilenceDetected(result.audioData, result.format);
          }
        }
      }
    } catch (err) {
      console.error('[useVoiceRecording] Error checking for silence:', err);
    }
  }, [isRecording, onSilenceDetected, stopRecordingInternal]);

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      audioChunksRef.current = [];
      lastSoundTimeRef.current = Date.now();

      // Request microphone access with optimal constraints for STT
      const constraints = {
        audio: {
          channelCount: 1, // Mono
          sampleRate: 16000, // 16kHz (optimal for STT)
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      mediaStreamRef.current = stream;
      setIsPermissionGranted(true);

      // Set up AudioContext for silence detection
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioContext;
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
      } catch (err) {
        console.warn('[useVoiceRecording] Failed to set up silence detection:', err);
        // Continue without silence detection
      }

      // Determine best audio format
      const options: MediaRecorderOptions = {
        mimeType: 'audio/webm;codecs=opus', // Preferred
        audioBitsPerSecond: 128000, // 128 kbps
      };

      // Fallback to WAV if WebM not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
        options.mimeType = 'audio/wav';
      }
      
      // Ensure mimeType is defined
      if (!options.mimeType) {
        options.mimeType = 'audio/webm;codecs=opus';
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      // Collect audio chunks
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('[useVoiceRecording] MediaRecorder error:', event);
        setError('Recording failed. Please try again.');
        stopRecordingInternal();
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Start silence detection interval
      if (analyserRef.current) {
        silenceCheckIntervalRef.current = window.setInterval(
          () => {
            checkForSilence().catch((err) => {
              console.error('[useVoiceRecording] Error in silence check:', err);
            });
          },
          VOICE_RECORDING_CONFIG.SILENCE_CHECK_INTERVAL
        );
      }
    } catch (err) {
      console.error('[useVoiceRecording] Failed to start recording:', err);
      setIsRecording(false);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError('Microphone permission denied. Please enable in browser settings.');
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          setError('Microphone not available. Please check your device.');
        } else {
          setError('Failed to start recording. Please try again.');
        }
      } else {
        setError('Failed to start recording. Please try again.');
      }
    }
  }, [checkForSilence, stopRecordingInternal]);

  /**
   * Stop recording and return base64 audio data (public API)
   */
  const stopRecording = useCallback(async (): Promise<{ audioData: string; format: string } | null> => {
    return stopRecordingInternal();
  }, [stopRecordingInternal]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Clear silence detection interval
      if (silenceCheckIntervalRef.current !== null) {
        clearInterval(silenceCheckIntervalRef.current);
      }

      // Stop recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      // Stop media stream
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Close AudioContext
      if (audioContextRef.current) {
        audioContextRef.current.close().catch((err) => {
          console.warn('[useVoiceRecording] Error closing AudioContext on unmount:', err);
        });
      }
    };
  }, []);

  return {
    isRecording,
    isPermissionGranted,
    error,
    startRecording,
    stopRecording,
    requestPermission,
    onSilenceDetected,
  };
}

